import { useState, useCallback } from 'react';
import tripActivityService from '../../../services/trips/trip-activity.service';
import { useTripData } from '../../contexts/trip-context';
import { ITripActivity } from '../../../Models/trips/trip-activity.model';
import { ITrip } from '../../../Models/trips/trip.model';

export interface IDeleteActivityModel {
  activityId: string;
  title: string;
  tripId: string;
}

export const useDeleteActivity = (
  activity: ITripActivity | null,
  onSuccess?: () => void
) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { trip, setTrip, removeActivityOptimistic } = useTripData();

  const deleteActivity = useCallback(async () => {
    if (!activity || !activity.id) {
      setError('Activity not found');
      return false;
    }

    const tripId = activity.tripId || trip?.id;
    if (!tripId) {
      setError('Trip ID not found');
      return false;
    }

    setIsDeleting(true);
    setError(null);

    // Snapshot current state for rollback, then apply optimistic removal
    const snapshot: ITrip = removeActivityOptimistic(activity.id);

    try {
      await tripActivityService.deleteActivity(tripId, activity.id);

      // Optimistic state already matches the API result — no refresh needed.
      onSuccess?.();
      return true;
    } catch (err: any) {
      console.error('Error deleting activity:', err);
      // Rollback optimistic update
      setTrip(snapshot);
      const errorMessage =
        err?.message || 'Failed to delete activity. Please try again.';
      setError(errorMessage);
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [activity, trip?.id, removeActivityOptimistic, setTrip, onSuccess]);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    deleteActivity,
    isDeleting,
    error,
    resetError,
    activityTitle: activity?.title || 'Unknown Activity',
    canDelete: !!(activity?.id && (activity.tripId || trip?.id))
  };
};
